import { register } from "../../../JavaScript/Utils";
import { AutoSuggestConfig, Company } from "./CompanySearch.types";

export const createAutoSuggest = (config: AutoSuggestConfig) => {

  const { el } = config;

  if (el.classList.contains('company-search-initialized')) {
    return;
  }

  el.classList.add('company-search-initialized');

  const apiUrl =
    "https://www.firmenwissen.de/search/suggest/companywithaddress";
  const authorization =
    "Basic QUZXLUJpa2VsZWFzaW5nOkJPR3VFTHdFWDRXeTlHYnJ1RTVK";

  function throwError() {
    throw new Error("Elements not found.");
  }

  const companyInput = el.querySelector<HTMLInputElement>(
    ".comp-company-search--company-field"
  );

  const modalTitleLabel = el.dataset.modalTitle;
  const modalDescription = el.dataset.modalDescription;
  const modalButtonLabel = el.dataset.modalButton;
  const modalInputPlaceholder = el.dataset.modalInputPlaceholder;

  let modalOpen = false;
  let currentSuggestionIndex = -1;

  let fieldStreet;
  let fieldZip;
  let fieldCity;
  let fieldCrefo;

  if (companyInput) {
    fieldZip = companyInput.getAttribute("data-zip");
    fieldStreet = companyInput.getAttribute("data-street");
    fieldCity = companyInput.getAttribute("data-city");
    fieldCrefo = companyInput.getAttribute("data-crefo");
  } else {
    throwError();
  }

  // get field-names for the CRM

  const zipInput = window.document.querySelector<HTMLInputElement>(
    `[name*=${fieldZip}]`
  );
  const streetInput = window.document.querySelector<HTMLInputElement>(
    `[name*=${fieldStreet}]`
  );
  const cityInput = window.document.querySelector<HTMLInputElement>(
    `[name*=${fieldCity}]`
  );
  const crefoInput = window.document.querySelector<HTMLInputElement>(
    `[name*=${fieldCrefo}]`
  );

  if (!companyInput || !zipInput || !streetInput || !cityInput) {
    throwError();
  }

  // Element Creator-Functions

  const createLoadingSpinnerContainer = (): HTMLElement => {
    const spinnerContainer = document.createElement("div");
    spinnerContainer.classList.add("spinner-container");
    return spinnerContainer;
  };

  const createSpinner = (): HTMLElement => {
    const spinner = document.createElement("div");
    spinner.classList.add("spinner");
    return spinner;
  };

  const createCloseButtonContainer = (): HTMLElement => {
    const closeButtonContainer = document.createElement("div");
    closeButtonContainer.classList.add("comp-company-search-close-container");
    return closeButtonContainer;
  };

  const createCloseButton = (): HTMLElement => {
    const closeButton = document.createElement("div");
    closeButton.classList.add("comp-company-search-close");
    closeButton.style.cursor = "pointer";
    return closeButton;
  };

  // Create dynamic Elements

  const modal = document.createElement("div");
  modal.className = "modal";
  const modalContent = document.createElement("div");
  modalContent.className = "modal-content";

  const closeButtonContainer = createCloseButtonContainer();
  modalContent.appendChild(closeButtonContainer);

  const closeButton = createCloseButton();
  closeButtonContainer.appendChild(closeButton);

  const form = document.createElement("form");
  form.className = "form-navigation";

  const inputWrapper = document.createElement("div");
  inputWrapper.className = "comp-company-search-input-wrapper";

  const companyInputModalRemove = document.createElement("span");
  companyInputModalRemove.className = "comp-company-search-company-icon-remove";

  const companyInputModal = document.createElement("input");
  companyInputModal.type = "text";
  companyInputModal.placeholder = modalInputPlaceholder;
  companyInputModal.className = "comp-company-search-company-field-modal";

  const title = document.createElement("label");
  title.innerHTML = modalTitleLabel;

  const description = document.createElement("p");
  description.innerHTML = modalDescription;

  const spinnerContainer = createLoadingSpinnerContainer();

  const spinner = createSpinner();

  const submitButton = document.createElement("button");
  submitButton.type = "submit";
  submitButton.textContent = modalButtonLabel;
  submitButton.classList.add("btn");
  submitButton.classList.add("btn--primary");

  const suggestionsContainer = document.createElement("div");
  suggestionsContainer.classList.add("comp-company-search-suggestions");

  form.appendChild(inputWrapper);
  inputWrapper.appendChild(companyInputModal);
  inputWrapper.appendChild(companyInputModalRemove);

  suggestionsContainer.appendChild(spinnerContainer);
  spinnerContainer.appendChild(spinner);
  form.appendChild(suggestionsContainer);
  form.appendChild(submitButton);

  modalContent.appendChild(description);
  modalContent.appendChild(title);
  modalContent.appendChild(form);

  modal.appendChild(modalContent);
  el.appendChild(modal);

  // Functions / Logic

  const getData = async (query = "") => {
    resetSuggestions();
    spinnerContainer.classList.add("is--active");
    const response = await fetch(`${apiUrl}/${query}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${authorization}`,
      },
      redirect: "follow",
    });
    spinnerContainer.classList.remove("is--active");
    return response.json();
  };

  const openModal = () => {
    if (!modalOpen) {
      modalOpen = true;
      el.classList.add("modal-open");
      modal.classList.add("is--active");
      companyInputModal.focus();
      const body = document.querySelector("body");
      if (body) {
        body.style.overflow = "hidden";
      }
    }
  };

  const resetSuggestions = () => {
    const elements = el.getElementsByClassName(
      "comp-company-search-suggestion"
    );

    while (elements.length > 0) {
      elements[0].parentNode.removeChild(elements[0]);
    }
  };

  const clearFields = () => {
    //cleanup fields when changing selections
    crefoInput.value = '';
    cityInput.value = '';
    streetInput.value = '';
    zipInput.value = '';
  };

  const closeModal = () => {
    const modal = el.querySelector(".modal");
    if (modal) {
      modalOpen = false;
      el.classList.remove("modal-open");
      modal.classList.remove("is--active");
      const body = document.querySelector("body");
      if (body) {
        body.style.overflow = "";
      }
    }
  };

  companyInput.addEventListener("focus", () => {
    openModal();
  });

  const showSuggestions = (suggestions: string[], companies: Company[]) => {
    suggestions.forEach((suggestion, index) => {
      const div = document.createElement("div");
      div.textContent = suggestion;
      div.classList.add("comp-company-search-suggestion");
      div.addEventListener("click", () => {
        selectSuggestion(suggestion, companies[index]);
        closeModal();
      });
      suggestionsContainer.appendChild(div);
    });
  };

  function selectSuggestion(suggestion: string, company: Company) {
    let companyName: string = company.name || ''
    if (companyInput.hasAttribute('maxlength')) {
      const maxChars: number = +companyInput.getAttribute('maxlength');
      companyName = companyName.substring(0, maxChars);
    }
    companyInputModal.value = companyName;
    companyInput.value = companyName;
    crefoInput.value = company.crefonummer || "";
    cityInput.value = company.ort || "";
    streetInput.value = company.strasseHausnummer || "";
    zipInput.value = company.plz || "";
  }
  const debounce = (fn: () => void, ms = 800) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), ms);
    };
  };

  const handleInput = () => {
    companyInput.value = companyInputModal.value;
    const value = companyInputModal.value;
    if (value.length > 2) {
      getData(`${value}`).then((answer) => {
        if (Array.isArray(answer.companyNameSuggestions)) {
          const suggestions = answer.companyNameSuggestions.map(
            (company: Company) =>
              `${company.name} ${company.strasseHausnummer} ${company.ort} ${company.plz}`
          );
          showSuggestions(suggestions, answer.companyNameSuggestions);
        }
      });
    }
  };

  const clearSuggestionSelection = () => {
    const suggestionElements = suggestionsContainer.querySelectorAll(
      ".comp-company-search-suggestion"
    );
    suggestionElements.forEach((element) => {
      element.classList.remove("selected");
    });
    clearFields();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const suggestionElements = suggestionsContainer.querySelectorAll(
      ".comp-company-search-suggestion"
    );
    clearFields();

    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        clearSuggestionSelection();
        currentSuggestionIndex =
          (currentSuggestionIndex - 1 + suggestionElements.length) %
          suggestionElements.length;
        suggestionElements[currentSuggestionIndex].classList.add("selected");
        break;
      case "ArrowDown":
        event.preventDefault();
        clearSuggestionSelection();
        currentSuggestionIndex =
          (currentSuggestionIndex + 1) % suggestionElements.length;
        suggestionElements[currentSuggestionIndex].classList.add("selected");
        break;
      case "Enter":
        if (currentSuggestionIndex !== -1) {
          event.preventDefault();
          const clickEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          suggestionElements[currentSuggestionIndex].dispatchEvent(clickEvent);
        }
        break;
    }
  };

  // Event-Listener

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    closeModal();
  });

  closeButton.addEventListener("click", () => closeModal());

  companyInputModal.addEventListener("input", debounce(handleInput));

  companyInputModal.addEventListener("keydown", handleKeyDown);

  companyInputModalRemove.addEventListener("click", () => {
    companyInputModal.value = "";
    companyInput.value = "";
    resetSuggestions();
    clearFields();
  });

  return {
    showSuggestions,
    selectSuggestion,
    handleInput,
  };
};

export const Autocomplete = async (el: HTMLElement) => {
  createAutoSuggest({ el });
};

register("Autocomplete", Autocomplete);
